import React from "react";
import { Button, Glyphicon, Modal, ControlLabel } from "react-bootstrap";
import translator from "../../../components/localization/translator";
import {
    SANITARY_BOOK_REJECT_REASONS,
    SANITARY_BOOK_STATUSES,
} from "../../../config/constants";
import { getFileName, getPages, getPageUrl, isImage, isPdf } from "./sanitaryBookHelpers";
import moment from "moment";

const RejectEditor = ({ shown, reason, onReasonChange, saving, onConfirm, onCancel }) => {
    if (!shown) {
        return null;
    }
    return (
        <div style={{ marginTop: 10, padding: 10, border: "1px solid #d9534f", borderRadius: 4 }}>
            <ControlLabel>{translator.translate("_sanitary_book_reject_reason_")} *</ControlLabel>
            <select
                className="form-control"
                style={{ maxWidth: 400 }}
                value={reason}
                onChange={(e) => onReasonChange(e.target.value)}>
                <option value="">{translator.translate("_choose_")}</option>
                {SANITARY_BOOK_REJECT_REASONS.REJECTION_REASONS.map(reasonItem => (
                    <option key={reasonItem.value} value={reasonItem.value}>
                        {translator.translate(reasonItem.label)}
                    </option>
                ))}
            </select>
            <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                <Button bsStyle="danger" disabled={saving} onClick={onConfirm}>
                    <Glyphicon glyph="remove" /> {translator.translate("_sanitary_book_confirm_reject_")}
                </Button>
                <Button bsStyle="default" disabled={saving} onClick={onCancel}>
                    {translator.translate("_cancel_")}
                </Button>
            </div>
        </div>
    );
};

const PageView = ({ page, index }) => {
    const url = getPageUrl(page);
    const name = getFileName(page, index);
    if (!url) {
        return <div style={{ marginBottom: 12 }}>{translator.translate("_no_data_")}</div>;
    }
    if (isImage(page)) {
        return (
            <div style={{ display: "inline-block", margin: 6, textAlign: "center" }}>
                <a href={url} target="_blank" rel="noopener noreferrer" title={name}>
                    <img src={url} alt={name}
                         style={{ width: 120, height: 120, objectFit: "cover", border: "1px solid #ddd" }} />
                </a>
                <div style={{ marginTop: 4, color: "#666", fontSize: 12 }}>{name}</div>
            </div>
        );
    }
    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 6, textAlign: "left" }}>
                <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-default btn-xs">
                    <Glyphicon glyph={isPdf(page) ? "file" : "link"} /> {name}
                </a>
            </div>
            <iframe title={name} src={url} style={{ width: "100%", height: 600, border: "1px solid #ddd" }} />
        </div>
    );
};

const SanitaryBookReviewModal = ({
                                     show,
                                     onClose,
                                     books,
                                     selectedIndex,
                                     onSelectBook,
                                     status,
                                     activeEndDate,
                                     onActiveEndDateChange,
                                     isReviewable,
                                     showActions,
                                     saving,
                                     onApprove,
                                     onOpenRejectEditor,
                                     onCloseRejectEditor,
                                     showRejectEditor,
                                     rejectReason,
                                     onRejectReasonChange,
                                     onConfirmReject,
                                 }) => {
    const book = books[selectedIndex];
    const pages = book ? getPages(book) : [];
    const isRejected = book && book.status === SANITARY_BOOK_STATUSES.REJECTED;
    const rejectionLabel = isRejected
        ? SANITARY_BOOK_REJECT_REASONS.reasonById(book.rejectReason)
        : null;

    const isLastBook = (index) => index === books.length - 1;

    return (
        <Modal bsSize="large" className="modal-centered" show={show} onHide={onClose}>
            <Modal.Header closeButton>
                <Modal.Title>{translator.translate("_sanitary_book_")}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="scrollable">
                {books.length > 1 && (
                    <div className="display-flex" style={{ gap: 4, marginBottom: 12, flexWrap: "wrap" }}>
                        {books.map((item, index) => {
                            const itemStatus = SANITARY_BOOK_STATUSES.statusById(item.status) || {};
                            const isLast = isLastBook(index);
                            const isPending = item.status === 1 || item.status === 2;
                            const activeEndDate = item.activeEndDate;

                            const isPreviousPending = !isLast && isPending;

                            return (
                                <Button
                                    key={index}
                                    bsSize="xsmall"
                                    bsStyle={index === selectedIndex ? "info" : "default"}
                                    title={translator.translate(itemStatus.name)}
                                    onClick={() => onSelectBook(index)}
                                    style={{
                                        opacity: isPreviousPending ? 0.7 : 1,
                                        fontStyle: isPreviousPending ? "italic" : "normal",
                                    }}
                                >
                                    {isPreviousPending ? `📄 ${moment(activeEndDate).format("YYYY-MM-DD")}` : ""}
                                    {isLast ? translator.translate("_sanitary_book_latest_") : null}
                                </Button>
                            );
                        })}
                    </div>
                )}
                <div className="display-flex align-start-center" style={{ marginBottom: 12 }}>
                    <ControlLabel className="label-width" style={{ fontWeight: 600 }}>
                        {translator.translate("_sanitary_book_status_")}:
                    </ControlLabel>
                    <span className={`label label-${status.className || "default"}`}>
                        {translator.translate(status.name)}
                    </span>
                </div>
                <div className="display-flex align-start-center" style={{ marginBottom: 12 }}>
                    <ControlLabel className="label-width" style={{ fontWeight: 600, marginBottom: 0 }}>
                        {translator.translate("_sanitary_book_validity_date_")}
                    </ControlLabel>
                    {isReviewable ? (
                        <input
                            type="date"
                            className="form-control"
                            style={{ maxWidth: 220 }}
                            min={new Date().toISOString().split("T")[0] || ""}
                            value={activeEndDate}
                            onChange={(e) => onActiveEndDateChange(e.target.value)}
                        />
                    ) : (
                        <span style={{ lineHeight: "34px" }}>
                            {activeEndDate || translator.translate("_no_data_")}
                        </span>
                    )}
                </div>
                {rejectionLabel && (
                    <div className="display-flex align-start-center" style={{ marginBottom: 12 }}>
                        <ControlLabel className="label-width" style={{ fontWeight: 600 }}>
                            {translator.translate("_sanitary_book_reject_reason_")}:
                        </ControlLabel>
                        <span style={{ color: "#d9534f", fontWeight: 500 }}>
                            {translator.translate(rejectionLabel.label)}
                        </span>
                    </div>
                )}
                {showActions && (
                    <div className="display-flex align-start-center" style={{ gap: 8 }}>
                        <Button bsStyle="success" disabled={saving || !isReviewable} onClick={onApprove}>
                            <Glyphicon glyph="ok" /> {translator.translate("_approve_")}
                        </Button>
                        <Button bsStyle="danger" disabled={saving || !isReviewable} onClick={onOpenRejectEditor}>
                            <Glyphicon glyph="remove" /> {translator.translate("_reject_")}
                        </Button>
                    </div>
                )}
                <RejectEditor
                    shown={showRejectEditor}
                    reason={rejectReason}
                    onReasonChange={onRejectReasonChange}
                    saving={saving}
                    onConfirm={onConfirmReject}
                    onCancel={onCloseRejectEditor} />
                <hr />
                <div style={{ textAlign: "center" }}>
                    {pages.length ? pages.map((page, index) => (
                        <PageView key={index} page={page} index={index} />
                    )) : (
                        <div>{translator.translate("_no_data_")}</div>
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default SanitaryBookReviewModal;
